<template>
  <div ref="container" class="relative overflow-hidden">
    <div
      v-if="showButtons && hasOverflowLeft"
      class="to-surface group absolute top-0 left-0 z-50 flex h-full w-14 items-center justify-start bg-linear-to-l from-transparent to-40% hover:bg-transparent"
    >
      <button
        class="group group-hover:bg-secondary-container/50 relative flex h-full w-10 items-center justify-center overflow-hidden rounded-full"
        @click="scrollLeft()"
      >
        <span class="material-symbols-outlined"> chevron_left </span>
        <BaseStateLayer />
      </button>
    </div>
    <div ref="innerContainer" class="no-scrollbar relative overflow-x-scroll">
      <slot :has-overflow="hasOverflow" />
    </div>

    <div
      v-if="showButtons && hasOverflowRight"
      class="to-surface group absolute top-0 right-0 z-50 flex h-full w-14 items-center justify-end bg-linear-to-r from-transparent to-40% hover:bg-transparent"
    >
      <button
        class="group group-hover:bg-secondary-container/50 relative flex h-full w-10 items-center justify-center overflow-hidden rounded-full"
        @click="scrollRight()"
      >
        <span class="material-symbols-outlined"> chevron_right </span>
        <BaseStateLayer />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch, type Ref } from 'vue'
  import { useMutationObserver, useScroll } from '@vueuse/core'
  import BaseStateLayer from '../state-layer/BaseStateLayer.vue'

  interface Props {
    activeHtml?: HTMLElement | null
    showButtons?: boolean
  }

  const props = defineProps<Props>()

  const container: Ref<HTMLDivElement | null> = ref(null)
  const innerContainer: Ref<HTMLDivElement | null> = ref(null)

  onMounted(() => {
    if (container.value && innerContainer.value) {
      const scrollContent = innerContainer.value
      if (scrollContent) {
        // Set up touch events for mobile devices
        let startX: number
        let currentX: number
        let touchStartX: number
        let touchCurrentX: number
        scrollContent.addEventListener(
          'touchstart',
          (e: TouchEvent) => {
            startX = scrollContent.scrollLeft
            touchStartX = e.touches[0].pageX
          },
          { passive: true },
        )
        scrollContent.addEventListener('touchmove', (e: TouchEvent) => {
          touchCurrentX = e.touches[0].pageX
          currentX = startX + (touchStartX - touchCurrentX)
          scrollContent.scrollLeft = currentX
        })

        let hasMoved = false

        // Set up mouse events for desktop devices
        let lastX: number
        scrollContent.addEventListener('mousedown', (e: MouseEvent) => {
          lastX = e.pageX
        })
        scrollContent.addEventListener('click', (e: MouseEvent) => {
          if (hasMoved) {
            e.stopPropagation()
          }
          hasMoved = false
        })
        scrollContent.addEventListener('mousemove', (e: MouseEvent) => {
          if (e.buttons === 1) {
            hasMoved = true
            currentX = scrollContent.scrollLeft + (lastX - e.pageX)
            scrollContent.scrollLeft = currentX
            lastX = e.pageX
          }
        })
      }
    }
  })

  watch(
    () => props.activeHtml,
    (newElement) => {
      if (newElement) {
        scrollToElement(newElement)
      }
    },
  )

  const scrollToElement = (element: HTMLElement) => {
    element.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })
  }

  const hasOverflow = computed(() => {
    return hasOverflowLeft.value || hasOverflowLeft.value
  })

  const { arrivedState, measure } = useScroll(innerContainer)

  useMutationObserver(
    innerContainer,
    () => {
      measure()
    },
    { childList: true, subtree: true },
  )

  const hasOverflowLeft = computed(() => {
    return !arrivedState.left
  })

  const hasOverflowRight = computed(() => {
    return !arrivedState.right
  })

  const buttonScrollOffset = computed(() => {
    if (innerContainer.value) {
      return (innerContainer.value.clientWidth / 3) * 2
    }
    return 200
  })

  const scrollRight = () => {
    if (innerContainer.value) {
      innerContainer.value.scrollTo({
        left: innerContainer.value.scrollLeft + buttonScrollOffset.value,
        behavior: 'smooth',
      })
    }
  }

  const scrollLeft = () => {
    if (innerContainer.value) {
      innerContainer.value.scrollTo({
        left: innerContainer.value.scrollLeft - buttonScrollOffset.value,
        behavior: 'smooth',
      })
    }
  }
</script>
